import { useState, useEffect, useMemo, useRef } from "react";
import { useScenario } from "../../state";
import { KPICard, Section, FlagItem } from "../../components";
import { Model } from "../../model";

interface ResolvedMap {
  ids: Set<string>;
  key: number;
}

export function ModelChecks() {
  const { active, result } = useScenario();
  const [lastRun, setLastRun] = useState(Date.now());
  const [flashKey, setFlashKey] = useState(0);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const prevLevelsRef = useRef<Record<string, string>>({});
  const [resolved, setResolved] = useState<ResolvedMap>({
    ids: new Set(),
    key: 0,
  });
  const checkSig = useMemo(
    () => JSON.stringify(active.assumptions),
    [active.assumptions],
  );

  useEffect(() => {
    setLastRun(Date.now());
    setFlashKey((k) => k + 1);
  }, [active.id, checkSig]);

  const checks = useMemo(
    () => Model.modelChecks(active, result),
    [active, result],
  );
  const pass = checks.filter((c) => c.level === "pass").length;
  const warn = checks.filter((c) => c.level === "warn").length;
  const fail = checks.filter((c) => c.level === "fail").length;

  // Detect newly-resolved checks for the pulse animation
  const resolvedIds = useMemo(() => {
    const ids = new Set<string>();
    for (const c of checks) {
      const prev = prevLevelsRef.current[c.id];
      if (prev && prev !== "pass" && c.level === "pass") {
        ids.add(c.id);
      }
    }
    // Update ref for next render
    const next: Record<string, string> = {};
    for (const c of checks) next[c.id] = c.level;
    prevLevelsRef.current = next;
    return ids;
  }, [checks]);
  // Clear resolved set after a tick so the animation plays once then disappears
  useEffect(() => {
    if (resolvedIds.size > 0) {
      const timer = setTimeout(() => {
        setResolved((r) => ({ ids: new Set(), key: r.key + 1 }));
      }, 700);
      return () => clearTimeout(timer);
    }
  }, [resolvedIds, resolved.key]);

  const ago = (() => {
    const s = Math.floor((Date.now() - lastRun) / 1000);
    if (s < 5) return "just now";
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    return `${Math.floor(s / 3600)}h ago`;
  })();

  const areas = [
    "Cost model",
    "Returns",
    "Cashflow model",
    "Operator",
    "Inputs",
    "Debt",
  ] as const;

  const badgeCls = (level: string) => {
    switch (level) {
      case "pass":
        return {
          dot: "hsl(var(--c-success))",
          bg: "hsl(var(--c-success) / 0.12)",
          text: "hsl(var(--c-success))",
        };
      case "warn":
        return {
          dot: "hsl(var(--c-warning))",
          bg: "hsl(var(--c-warning) / 0.12)",
          text: "hsl(var(--c-warning))",
        };
      default:
        return {
          dot: "hsl(var(--c-destructive))",
          bg: "hsl(var(--c-destructive) / 0.12)",
          text: "hsl(var(--c-destructive))",
        };
    }
  };

  return (
    <div className="page fade-in">
      <div className="page-strip">
        <div>
          <div className="eyebrow" style={{ marginBottom: 4 }}>
            Self-audit
          </div>
          <h2 className="h3" style={{ margin: 0 }}>
            Model checks
          </h2>
          <div className="lede" style={{ marginTop: 6 }}>
            Recomputed formulas tied back to the model's stated values. Anything
            that doesn't tie is a real issue, not a tolerance.
          </div>
          <div className="p-small" style={{ marginTop: 8 }}>
            <strong>{pass} passed</strong> · {warn} watch
            {warn === 1 ? "" : "es"} ·{" "}
            <span style={{ color: "hsl(var(--c-destructive))" }}>
              {fail} failed
            </span>{" "}
            — expand each area to see the detail.
          </div>
        </div>
        <div className="row gap-2" style={{ alignItems: "center" }}>
          <span
            className="p-micro"
            style={{ color: "hsl(var(--c-foreground-muted))" }}
          >
            Checked {ago}
          </span>
        </div>
      </div>

      <div className="grid g-3" style={{ position: "relative" }}>
        <div
          key={flashKey}
          className="pulse-flash"
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            borderRadius: "inherit",
            zIndex: 1,
          }}
        />
        <KPICard
          eyebrow="Passed"
          value={String(pass)}
          status="good"
          foot={`of ${checks.length} checks`}
        />
        <KPICard
          eyebrow="Watch"
          value={String(warn)}
          status={warn ? "warn" : "good"}
          foot="Coherence flags"
        />
        <KPICard
          eyebrow="Failed"
          value={String(fail)}
          status={fail ? "bad" : "good"}
          foot="Hard mismatches"
        />
      </div>

      {fail > 0 && (
        <div
          className="card model-check-fail-summary"
          style={{
            border: "1px solid hsl(var(--c-destructive) / 0.5)",
            background: "hsl(var(--c-destructive) / 0.08)",
          }}
        >
          <div className="col" style={{ gap: 10, padding: "14px 16px" }}>
            {checks
              .filter((c) => c.level === "fail")
              .map((c) => (
                <div
                  key={c.id}
                  className="row gap-3"
                  style={{ alignItems: "flex-start" }}
                >
                  <span
                    className="status-dot"
                    style={{
                      marginTop: 5,
                      flexShrink: 0,
                      background: "hsl(var(--c-destructive))",
                    }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>
                      {c.label}
                    </div>
                    <div
                      className="p-micro"
                      style={{
                        marginTop: 2,
                        color: "hsl(var(--c-foreground-muted))",
                        lineHeight: 1.45,
                      }}
                    >
                      {c.detail}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      <div className="card elevated">
        {areas.map((area, ai) => {
          const items = checks.filter((c) => c.area === area);
          if (items.length === 0) return null;
          const hasFail = items.some((c) => c.level === "fail");
          const hasWarn = items.some((c) => c.level === "warn");
          const isOpen = open[area] ?? (hasFail || hasWarn);
          const areaId = area.toLowerCase().replace(/[^a-z0-9]+/g, "-");
          return (
            <div
              key={area}
              className="model-check-area"
              style={{
                borderBottom:
                  ai < areas.length - 1
                    ? "1px solid hsl(var(--c-border-subtle))"
                    : undefined,
              }}
            >
              <button
                type="button"
                onClick={() => setOpen((o) => ({ ...o, [area]: !isOpen }))}
                aria-expanded={isOpen}
                aria-controls={`mc-area-${areaId}`}
                aria-label={`${area} checks — ${isOpen ? "collapse" : "expand"}`}
                className="model-check-area-header row between"
              >
                <div className="row gap-2" style={{ alignItems: "center" }}>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{area}</span>
                  <span
                    className="p-micro"
                    style={{ color: "hsl(var(--c-foreground-muted))" }}
                  >
                    {items.length} checks
                  </span>
                  {hasFail ? (
                    <span className="pill danger" style={{ fontSize: 10 }}>
                      FAIL
                    </span>
                  ) : hasWarn ? (
                    <span className="pill warn" style={{ fontSize: 10 }}>
                      WARN
                    </span>
                  ) : null}
                </div>
                <span
                  className="model-check-chevron"
                  style={{ transform: isOpen ? "rotate(180deg)" : "none" }}
                >
                  ▼
                </span>
              </button>
              {isOpen ? (
                <div
                  id={`mc-area-${areaId}`}
                  style={{ padding: "0 14px 10px" }}
                >
                  <div className="table-scroll">
                    <table className="table model-check-table">
                      <tbody>
                        {items.map((c) => {
                          const b = badgeCls(c.level);
                          return (
                            <tr key={c.id}>
                              <td
                                style={{ width: 1, padding: "6px 8px 6px 0" }}
                              >
                                <span
                                  style={{
                                    display: "inline-block",
                                    width: 7,
                                    height: 7,
                                    borderRadius: "50%",
                                    background: b.dot,
                                  }}
                                />
                              </td>
                              <td style={{ padding: "6px 0", fontWeight: 500 }}>
                                {c.label}
                              </td>
                              <td
                                style={{
                                  padding: "6px 0 6px 16px",
                                  color: "hsl(var(--c-foreground-muted))",
                                  fontSize: 12,
                                }}
                              >
                                {c.detail}
                              </td>
                              <td
                                style={{
                                  width: 1,
                                  padding: "6px 0 6px 12px",
                                  textAlign: "right",
                                }}
                              >
                                <span
                                  className={`pill${resolvedIds.has(c.id) ? " check-resolved" : ""}`}
                                  style={{
                                    fontSize: 9,
                                    letterSpacing: "0.06em",
                                    fontWeight: 700,
                                    background: b.bg,
                                    color: b.text,
                                    border: "none",
                                  }}
                                >
                                  {c.level.toUpperCase()}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {result.flags.length > 0 ? (
        <Section eyebrow="Active flags" title="Raised this run">
          <div className="col" style={{ gap: 8 }}>
            {result.flags.map((f, idx) => (
              <FlagItem key={`${f.message}-${idx}`} {...f} />
            ))}
          </div>
        </Section>
      ) : null}
    </div>
  );
}
